import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import history from '../utils/history';
import { capitalizeFirstLetter, checkIfHotjarEnable, timeBetweenDates } from '../utils/helpers';
import { coveragePlans } from '../data/coveragePlan';
import * as sinisterCardsActions from '../redux/actions/sinister-cards';
import InfoCard from '../components/content/InfoCard/InfoCard';
import RimacTag from '../components/common/RimacTag/RimacTag';
import IconWarning from '../assets/ic_warning.svg';
import ModalFinal from '../assets/modalFinal.svg';
import SplitLayout from '../layout/SplitLayout';
import { PAGINAS_BY_STEP } from '../utils/constants';

const infoProps = {
  adIcon: IconWarning,
  textAd:
    'Recuerda estar atento a tu correo electrónico, un asesor de Rimac se comunicará contigo para verificar esta información.',
  firstQuestion:
    '¿Tienes contratado otro seguro de Protección de Tarjetas con otra Entidad Bancaria o Aseguradora?',
  secondQuestion: '¿Has solicitado la cobertura para este mismo siniestro?',
  modalIcon: ModalFinal,
  modalText:
    'Al hacer clic en el botón enviar confirmas que la información provista es cierta y tiene carácter de declaración jurada.',
  modalTitle: 'Datos declarados en el formulario',
};

const InsuranceInfoPage = () => {
  const dispatch = useDispatch();
  const otherInsuranceStatus = useSelector(
    (state) => state.sinisterCards.otherInsurance
  );
  const currentCoveragePlan = useSelector(
    (state) => state.coverages.currentPlan
  );
  const sinisterList = useSelector((state) => state.sinisterCards.sinisterList);
  const currentUser = useSelector((state) => state.sinisterCards.userInfo);
  const currentUserName = `${capitalizeFirstLetter(
    currentUser?.name ?? ''
  )} ${capitalizeFirstLetter(currentUser?.fatherLastName ?? '')}`;
  const [viewStartTime] = useState(new Date());
  const [first, setFirst] = useState(otherInsuranceStatus.otherInsurance);
  const [sec, setSec] = useState(otherInsuranceStatus.coverage);

  useEffect(() => {
    if (!viewStartTime) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    const endTime = new Date();
    const userTotalTime = timeBetweenDates(viewStartTime, endTime);

    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        task: {
          name: 'Tiempo registrado - Otro Banco',
          time: userTotalTime,
        },
        event: 'trackTimeToComplete',
      });
    }
  }, [viewStartTime]);

  useEffect(() => {
    if (!currentCoveragePlan) return
    if (!first) return
    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        "metadata": {
          "key": "Protección Tarjeta BBVA - Seguro externo",
          "value": `${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())} - ${first === 'no'? 'No': 'Sí'}`
        },
        "event": "trackMetadata"
      });
    }
  }, [currentCoveragePlan, first]);

  useEffect(() => {
    if (!currentCoveragePlan) return
    if (!sec) return
    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        "metadata": {
          "key": "Protección Tarjeta BBVA - Cobertura Seguro externo",
          "value": `${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())} - ${sec === 'no'? 'No': 'Sí'}`
        },
        "event": "trackMetadata"
      });
    }
  }, [currentCoveragePlan, sec]);

  const handleFirst = (e) => {
    setFirst(e.target.value);
  };

  const handleSec = (e) => {
    setSec(e.target.value);
  };

  const validateForm = () => {
    return !(first === 'no' || (first === 'si' && sec));
  };

  const onBackHandler = () => {
    dispatch(sinisterCardsActions.setStep({step: PAGINAS_BY_STEP.DETALLE_SINIESTRO.step, plan: currentCoveragePlan}));
    history.replace(`/${coveragePlans[currentCoveragePlan].uri}/detalle-siniestro`);
  };

  const onContinueHandler = () => {
    dispatch(
      sinisterCardsActions.setOtherInsurance({
        otherInsurance: first,
        coverage: sec,
      })
    );
    dispatch(sinisterCardsActions.setStep({step: PAGINAS_BY_STEP.ANTES_EMPEZAR.step}));
    history.replace(`/${coveragePlans[currentCoveragePlan].uri}/antes-de-comenzar`);
  };

  const leftContent = (
    <>
      <RimacTag
        customClasses="u-mb-8"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <div className="titleCont">
        <h1 className="u-mb-40">Seguro de Protección de Tarjetas</h1>
        <p>
          <span className="u-font-bold">{currentUserName}</span>, estás
          solicitando:
        </p>
        <ul className="rimacCardCont">
          {sinisterList.map((cov) => (
            <div key={cov.code} className="rimacLiteCard u-mb-8">
              <img
                className="rimacLiteCard__img"
                src={cov.icon}
                alt={cov.title}
              />
              <p className="rimacLiteCard__title">{cov.title}</p>
            </div>
          ))}
        </ul>
      </div>
    </>
  );

  const rightContent = (
    <>
      <div>
        <p className="other-insurance__question">{infoProps.firstQuestion}</p>
        <div className="other-insurance__answer">
          <label className="container">
            Si
            <input
              type="radio"
              name="firstQuestion"
              onChange={handleFirst}
              value="si"
              checked={first === 'si'}
            />
            <span className="checkmark"></span>
          </label>
          <label className="container">
            No
            <input
              type="radio"
              name="firstQuestion"
              onChange={handleFirst}
              value="no"
              checked={first === 'no'}
            />
            <span className="checkmark"></span>
          </label>
        </div>
      </div>

      {first === 'si' && (
        <>
          <p className="other-insurance__question">
            {infoProps.secondQuestion}
          </p>
          <div className="other-insurance__answer">
            <label className="container">
              Si
              <input
                type="radio"
                name="secondQuestion"
                onChange={handleSec}
                value="si"
                checked={sec === 'si'}
              />
              <span className="checkmark"></span>
            </label>
            <label className="container">
              No
              <input
                type="radio"
                name="secondQuestion"
                onChange={handleSec}
                value="no"
                checked={sec === 'no'}
              />
              <span className="checkmark"></span>
            </label>
          </div>
          {sec === 'si' && (
            <InfoCard icon={IconWarning} content={infoProps.textAd} />
          )}
        </>
      )}
    </>
  );

  return (
    <SplitLayout
      dataTestId='insurance-info-page'
      containerClasses="insuranceInfoPage"
      leftClasses="insuranceInfoPage__left"
      rightClasses="insuranceInfoPage__right"
      leftContent={leftContent}
      rightContent={rightContent}
      onNavContinueClick={onContinueHandler}
      onNavBackClick={onBackHandler}
      navCurrentStep={1}
      navTotalSteps={5}
      isNavActive={true}
      isNavNextDisabled={validateForm()}
      navStepperText={'PASO 1 DE 5'}
    />
  );
};

export default InsuranceInfoPage;
